<template>
  <div class="service-records-shell">
    <Toast />
    <div class="service-records-shell__container">
      <!-- Documentation and Services Tabs Section -->
      <section
        v-if="isValidTender"
        class="service-records-shell__documentation"
      >
        <ServiceRecordsDocumentation
          :tender="tender!"
          :service-offer-id="serviceOfferId!"
          :supplier-id="supplierId!"
          :supplier-request-id="supplierRequestId!"
          :editing-record="editingRecord"
          :accepted-service-offer="acceptedServiceOffer"
          :local-documentation="localDocumentation"
          @new-service-record="handleNewServiceRecord"
          @active-tab-changed="handleActiveTabChanged"
          @documentation-submitted="handleDocumentationSubmitted"
        />
      </section>

      <!-- Documentation List Section -->
      <section
        v-if="activeTab === 'dokumenter'"
        class="service-records-shell__records"
      >
        <div class="service-records-shell__records-content">
          <DocumentationList
            v-if="isValidTender"
            :service-offer-id="serviceOfferId!"
            :tender-id="tender!.tenderId"
            :customer-org-number="tender!.customerOrganization.orgNumber"
            :supplier-id="supplierId!"
            :accepted-service-offer="props.acceptedServiceOffer"
            :local-documentation="localDocumentation"
          />
        </div>
      </section>

      <!-- Service Records List Section -->
      <section
        v-if="activeTab === 'service'"
        class="service-records-shell__records"
      >
        <div class="service-records-shell__records-content">
          <ServiceRecordsList
            v-if="isValidTender"
            :service-offer-id="serviceOfferId!"
            :tender-id="tender!.tenderId"
            :customer-org-number="tender!.customerOrganization.orgNumber"
            :supplier-id="supplierId!"
            :service-records="serviceRecords"
            @delete-service-record="handleDeleteServiceRecord"
            @update-service-record="handleUpdateServiceRecord"
            @mark-complete="handleMarkComplete"
          />
        </div>
      </section>
    </div>

    <!-- Edit Confirmation Dialog -->
    <Dialog
      :visible="showEditDialog"
      modal
      header="Rediger serviceoppføring"
      :style="{ width: '500px' }"
      @update:visible="showEditDialog = $event"
    >
      <div class="confirmation-content">
        <i class="pi pi-info-circle confirmation-icon" />
        <div>
          <p>Er du sikker på at du vil redigere denne serviceoppføringen?</p>
          <p class="confirmation-date">{{ formatEditRecordDate }}</p>
        </div>
      </div>
      <template #footer>
        <BaseButton
          text="Avbryt"
          variant="tertiary"
          :uppercase="true"
          @click="cancelEdit"
        />
        <BaseButton text="Rediger" :uppercase="true" @click="confirmEdit" />
      </template>
    </Dialog>

    <!-- Mark Complete Confirmation Dialog -->
    <Dialog
      :visible="showMarkCompleteDialog"
      modal
      header="Marker som fullført"
      :style="{ width: '600px' }"
      @update:visible="showMarkCompleteDialog = $event"
    >
      <div class="confirmation-content">
        <i class="pi pi-check-circle confirmation-icon" />
        <div class="confirmation-text">
          <p>
            Er du sikker på at du vil markere denne serviceoppføringen som
            fullført?
          </p>
          <p class="confirmation-date">
            {{ formatMarkCompleteRecordDate }}
          </p>
          <p class="confirmation-warning">
            <strong>Merk:</strong> Denne handlingen kan ikke angres.
          </p>
        </div>
      </div>

      <div v-if="needsDocumentUpload" class="document-upload-section">
        <h4>Last opp dokumentasjon</h4>
        <p class="upload-description">
          Du må laste opp et dokument som dokumenterer at tjenesten er fullført.
        </p>

        <FileUpload
          mode="basic"
          :auto="false"
          choose-label="Velg dokument"
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
          :max-file-size="10000000"
          :class="{ 'p-invalid': documentUploadError }"
          @select="handleDocumentSelect"
          @remove="handleDocumentRemove"
        />

        <div v-if="completionDocument" class="selected-file">
          <i class="pi pi-file" />
          <span>{{ completionDocument.name }}</span>
          <button class="remove-file-btn" @click="handleDocumentRemove">
            <i class="pi pi-times" />
          </button>
        </div>

        <div v-if="documentUploadError" class="error-message">
          {{ documentUploadError }}
        </div>
      </div>

      <div v-else class="existing-attachments-section">
        <h4>Eksisterende dokumentasjon</h4>
        <p class="attachments-description">
          Denne serviceoppføringen har allerede dokumentasjon vedlagt.
        </p>
        <div class="attachments-list">
          <div
            v-for="attachment in pendingMarkCompleteRecord?.attachments"
            :key="attachment.attachmentId"
            class="attachment-item"
          >
            <i class="pi pi-file" />
            <span>{{ attachment.fileName }}</span>
          </div>
        </div>
      </div>

      <template #footer>
        <BaseButton
          text="Avbryt"
          variant="tertiary"
          :uppercase="true"
          @click="showMarkCompleteDialog = false"
        />
        <BaseButton
          text="Marker som fullført"
          :uppercase="true"
          :loading="isMarkCompleteLoading"
          :disabled="
            isMarkCompleteLoading ||
            (needsDocumentUpload && !completionDocument)
          "
          @click="confirmMarkComplete"
        />
      </template>
    </Dialog>

    <!-- Delete Confirmation Dialog -->
    <Dialog
      :visible="showDeleteDialog"
      modal
      header="Bekreft sletting"
      :style="{ width: '450px' }"
      @update:visible="showDeleteDialog = $event"
    >
      <div class="confirmation-content">
        <i class="pi pi-exclamation-triangle confirmation-icon delete-icon" />
        <div>
          <p>Er du sikker på at du vil slette denne serviceoppføringen?</p>
          <p class="confirmation-warning">
            <strong>Merk:</strong> Denne handlingen kan ikke angres.
          </p>
        </div>
      </div>
      <template #footer>
        <BaseButton
          text="Avbryt"
          variant="tertiary"
          :uppercase="true"
          @click="showDeleteDialog = false"
        />
        <BaseButton
          text="Slett"
          variant="primary"
          :uppercase="true"
          :loading="isDeleteLoading"
          :disabled="isDeleteLoading"
          @click="confirmDelete"
        />
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from "vue";
import ServiceRecordsList from "./ServiceRecordsList.vue";
import ServiceRecordsDocumentation from "./ServiceRecordsDocumentation.vue";
import DocumentationList from "./DocumentationList.vue";
import Dialog from "primevue/dialog";
import Toast from "primevue/toast";
import FileUpload from "primevue/fileupload";
import BaseButton from "@/components/Base/BaseButton/BaseButton.vue";
import { useServiceRecordsApi } from "@/services/api/useServiceRecordsApi";
import { useServiceRecordsCache } from "@/composables/useServiceRecordsCache";
import { formatDateInNorwegian } from "@/utilities/dateUtils";
import { convertFileToBase64 } from "@/utilities/fileUtils";
import type {
  ServiceRecord,
  DocumentationRecord,
  FileAttachment,
} from "@/types/service-records";
import type { Tender } from "@/stores/tender/types";
import type { ServiceOffer } from "@/custom-types/serviceOffers";

interface Props {
  tender: Tender | null;
  supplierId: number | null;
  serviceOfferId: number | null;
  supplierRequestId?: number;
  existingDocumentation?: DocumentationRecord | null;
  acceptedServiceOffer?: ServiceOffer | null;
}

const props = defineProps<Props>();

// Reactive state
const serviceRecords = ref<ServiceRecord[] | null>(null);
const activeTab = ref<string>("dokumenter");
const editingRecord = ref<ServiceRecord | null>(null);
const showEditDialog = ref(false);
const showMarkCompleteDialog = ref(false);
const showDeleteDialog = ref(false);
const pendingEditRecord = ref<ServiceRecord | null>(null);
const pendingMarkCompleteRecord = ref<ServiceRecord | null>(null);
const pendingDeleteRecord = ref<ServiceRecord | null>(null);
const isMarkCompleteLoading = ref(false);
const isDeleteLoading = ref(false);
const completionDocument = ref<File | null>(null);
const documentUploadError = ref<string>("");

// Documentation state
const localDocumentation = ref<DocumentationRecord | null>(null);

// Composables
const {
  fetchServiceRecords,
  updateServiceRecord,
  deleteServiceRecord,
  markServiceRecordComplete,
} = useServiceRecordsApi();

const {
  getCachedServiceRecords,
  setCachedServiceRecords,
  clearCachedServiceRecords,
} = useServiceRecordsCache();

// Computed properties
const isValidTender = computed(() => {
  const result =
    props.tender && props.supplierId !== null && props.serviceOfferId !== null;
  return result;
});

const formatEditRecordDate = computed(() => {
  if (!pendingEditRecord.value?.serviceDate) return "";
  return formatDateInNorwegian(
    pendingEditRecord.value.serviceDate,
    "DD.MM.YYYY",
  );
});

const formatMarkCompleteRecordDate = computed(() => {
  if (!pendingMarkCompleteRecord.value?.serviceDate) return "";
  return formatDateInNorwegian(
    pendingMarkCompleteRecord.value.serviceDate,
    "DD.MM.YYYY",
  );
});

const hasExistingAttachments = computed(() => {
  return (
    pendingMarkCompleteRecord.value?.attachments &&
    pendingMarkCompleteRecord.value.attachments.length > 0
  );
});

const needsDocumentUpload = computed(() => {
  return !hasExistingAttachments.value;
});

// Watchers for debugging
watch(
  () => localDocumentation.value,
  (newDoc, oldDoc) => {
    console.log("👀 ServiceRecordsShell.localDocumentation changed:");
    console.log("  Old:", oldDoc);
    console.log("  New:", newDoc);
  },
  { deep: true },
);

// Critical watcher: Monitor props changes and refetch when they become valid
watch(
  () => [props.tender, props.supplierId, props.serviceOfferId],
  (newValues, oldValues) => {
    console.log("👀 ServiceRecordsShell props changed:", newValues, oldValues);
    // If props became valid and we don't have service records yet, fetch them
    if (isValidTender.value && serviceRecords.value === null) {
      fetchServiceRecordsData();
    }
  },
  {
    immediate: false, // Don't trigger on mount (onMounted handles that)
    deep: false, // Simple value comparison is sufficient
  },
);

// Additional watcher for isValidTender computed property
watch(
  () => isValidTender.value,
  (newValid, oldValid) => {
    if (newValid && !oldValid && serviceRecords.value === null) {
      fetchServiceRecordsData();
    }
  },
  { immediate: false },
);

// Event handlers
const handleNewServiceRecord = async () => {
  // Clear cache when new service records are created
  if (props.supplierId && props.serviceOfferId) {
    clearCachedServiceRecords(props.supplierId, props.serviceOfferId);
  }
  await fetchServiceRecordsData();
  editingRecord.value = null; // Clear editing state
};

const handleActiveTabChanged = (tabValue: string) => {
  activeTab.value = tabValue;
};

const handleDocumentationSubmitted = (documentation: DocumentationRecord) => {
  // Update local documentation to show immediate feedback
  localDocumentation.value = documentation;
};

const handleDeleteServiceRecord = async (serviceRecordId: number) => {
  // Find the record to show in confirmation
  const record = serviceRecords.value?.find((r) => r.id === serviceRecordId);
  if (record) {
    pendingDeleteRecord.value = record;
    showDeleteDialog.value = true;
  }
};

const handleUpdateServiceRecord = async (record: ServiceRecord) => {
  pendingEditRecord.value = record;
  showEditDialog.value = true;
};

const confirmEdit = () => {
  if (pendingEditRecord.value) {
    editingRecord.value = pendingEditRecord.value;
    activeTab.value = "service"; // Switch to service tab to show the form
    showEditDialog.value = false;
    pendingEditRecord.value = null;
  }
};

const cancelEdit = () => {
  showEditDialog.value = false;
  pendingEditRecord.value = null;
  editingRecord.value = null; // Clear the editing record
};

const handleMarkComplete = async (serviceRecordId: number) => {
  // Find the record to show in confirmation
  const record = serviceRecords.value?.find((r) => r.id === serviceRecordId);
  if (record) {
    pendingMarkCompleteRecord.value = record;
    // Reset document state
    completionDocument.value = null;
    documentUploadError.value = "";
    showMarkCompleteDialog.value = true;
  }
};

const confirmMarkComplete = async () => {
  if (!isValidTender.value || !pendingMarkCompleteRecord.value) return;

  // Check if record has existing attachments or a new document is uploaded
  if (!hasExistingAttachments.value && !completionDocument.value) {
    documentUploadError.value =
      "Du må laste opp et dokument før du kan markere oppføringen som fullført.";
    return;
  }

  try {
    isMarkCompleteLoading.value = true;

    // If a new document was uploaded, update the service record first
    if (completionDocument.value) {
      const fileContent = await convertFileToBase64(completionDocument.value);
      const newAttachment: FileAttachment = {
        fileName: completionDocument.value.name,
        fileContent,
      };

      // Update service record with new attachment
      await updateServiceRecord(
        pendingMarkCompleteRecord.value.id,
        props.supplierId!,
        props.tender!.customerOrganization.orgNumber,
        {
          serviceDate: pendingMarkCompleteRecord.value.serviceDate,
          nextServiceDate: pendingMarkCompleteRecord.value.nextServiceDate,
          comment: pendingMarkCompleteRecord.value.comment,
          attachments: [newAttachment],
        },
      );
    }

    // Then mark the service record as complete
    await markServiceRecordComplete(
      pendingMarkCompleteRecord.value.id,
      props.supplierId!,
      props.tender!.customerOrganization.orgNumber,
    );

    await fetchServiceRecordsData();
    showMarkCompleteDialog.value = false;
    pendingMarkCompleteRecord.value = null;
    completionDocument.value = null;
    documentUploadError.value = "";
  } catch (error) {
    console.error("Failed to mark service record complete:", error);
    documentUploadError.value =
      "Kunne ikke markere oppføringen som fullført. Prøv igjen.";
  } finally {
    isMarkCompleteLoading.value = false;
  }
};
const confirmDelete = async () => {
  if (!isValidTender.value || !pendingDeleteRecord.value) return;

  try {
    isDeleteLoading.value = true;
    await deleteServiceRecord(
      pendingDeleteRecord.value.id,
      props.supplierId!,
      props.tender!.customerOrganization.orgNumber,
    );
    await fetchServiceRecordsData();
    showDeleteDialog.value = false;
    pendingDeleteRecord.value = null;
  } catch (error) {
    console.error("Failed to delete service record:", error);
  } finally {
    isDeleteLoading.value = false;
  }
};

const handleDocumentSelect = (event: { files: File[] }) => {
  const file = event.files[0];
  if (file) {
    completionDocument.value = file;
    documentUploadError.value = "";
  }
};

const handleDocumentRemove = () => {
  completionDocument.value = null;
  documentUploadError.value = "";
};

// Data fetching
const fetchServiceRecordsData = async () => {
  if (!isValidTender.value) {
    // Try to load from cache even if props aren't ready yet
    if (props.supplierId && props.serviceOfferId) {
      const cached = getCachedServiceRecords(
        props.supplierId,
        props.serviceOfferId,
      );
      if (cached) {
        serviceRecords.value = cached.serviceRecords;
        return;
      }
    }

    return;
  }

  try {
    const cached = getCachedServiceRecords(
      props.supplierId!,
      props.serviceOfferId!,
    );
    if (cached) {
      serviceRecords.value = cached.serviceRecords;
      return;
    }

    serviceRecords.value = null; // Set loading state

    const response = await fetchServiceRecords({
      supplierId: props.supplierId!,
      offerId: props.serviceOfferId!,
    });

    const serviceRecordsData = response?.data || [];
    serviceRecords.value = serviceRecordsData;

    // Cache the response for future use
    if (serviceRecordsData.length > 0 || response?.data) {
      setCachedServiceRecords({
        serviceRecords: serviceRecordsData,
        createdAt: new Date().toISOString(),
        supplierId: props.supplierId!,
        serviceOfferId: props.serviceOfferId!,
      });
    }
  } catch (error) {
    console.error("❌ Failed to fetch service records:", error);
    serviceRecords.value = [];
  }
};

// Lifecycle
onMounted(() => {
  fetchServiceRecordsData();
});
</script>

<style lang="scss" scoped>
.service-records-shell {
  width: 100%;

  &__container {
    display: flex;
    flex-direction: column;
    gap: 2rem;
    max-width: 1060px;
    margin: auto;

    @media (max-width: $bp-sm) {
      padding: 1rem;
      gap: 1.5rem;
    }
  }

  &__documentation {
    background: $color-white;
    overflow: hidden;
    padding: 0.5rem;
  }

  &__records {
    background: $color-white;
    padding: 0.5rem;

    &-content {
      @media (max-width: $bp-sm) {
        padding: 1rem;
      }
    }
  }
}

.confirmation-content {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  margin-bottom: 1.5rem;

  .confirmation-icon {
    color: $color-info;
    font-size: 1.25rem;
    flex-shrink: 0;
    margin-top: 0.125rem;

    &.delete-icon {
      color: $color-warning;
    }
  }

  .confirmation-text {
    flex: 1;
  }

  .confirmation-date {
    margin: 0.5rem 0;
    line-height: 1.5;
    color: $color-text-secondary;
    font-weight: 500;
    font-size: 0.9rem;
  }

  .confirmation-warning {
    margin: 0.75rem 0;
    line-height: 1.5;
    color: $color-black;
    font-size: 14px;
  }

  p {
    margin: 0 0 0.5rem 0;
    line-height: 1.5;
  }
}

.document-upload-section {
  border-top: 1px solid $color-border-light;
  padding-top: 1.5rem;
  margin-top: 1rem;

  h4 {
    margin: 0 0 0.5rem 0;
    color: $color-text-primary;
    font-weight: 600;
  }

  .upload-description {
    margin: 0 0 1rem 0;
    color: $color-text-secondary;
    font-size: 0.9rem;
    line-height: 1.4;
  }

  .selected-file {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 1rem;
    padding: 0.75rem;
    background: $color-grey-lighter;
    border: 1px solid $color-border-light;
    border-radius: 0.375rem;

    .pi-file {
      color: $color-primary;
    }

    span {
      flex: 1;
      font-size: 0.9rem;
    }

    .remove-file-btn {
      background: none;
      border: none;
      color: $color-text-secondary;
      cursor: pointer;
      padding: 0.25rem;
      border-radius: 0.25rem;
      transition: color 0.2s ease;

      &:hover {
        color: $color-error;
      }
    }
  }

  .error-message {
    margin-top: 0.5rem;
    color: $color-error;
    font-size: 0.9rem;
    font-weight: 500;
  }
}

.existing-attachments-section {
  border-top: 1px solid $color-border-light;
  padding-top: 1.5rem;
  margin-top: 1rem;

  h4 {
    margin: 0 0 0.5rem 0;
    color: $color-text-primary;
    font-weight: 600;
    font-size: 16px;
  }

  .attachments-description {
    margin: 0 0 1rem 0;
    color: $color-text-secondary;
    font-size: 14px;
  }

  .attachments-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .attachment-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem;
    background: $color-grey-lighter;
    border: 1px solid $color-border-light;
    border-radius: 0.375rem;

    .pi-file {
      color: $color-primary;
    }

    span {
      flex: 1;
      font-size: 0.9rem;
    }
  }
}
</style>
